import { type PathParam, Navigate, generatePath } from 'react-router-dom';

import { usePermission } from '../../providers';

import { type Params, type PermissionRouteData } from './types';

export interface PermissionRouteProps<TPath extends string, TParams extends Params> {
  routes: PermissionRouteData<TPath, TParams>[];
}

export default function PermissionRoute<TPath extends string, TParams extends Params>(
  props: Readonly<PermissionRouteProps<TPath, TParams>>,
) {
  const { permissions } = usePermission();
  const { routes } = props;

  const matchedRoute = routes.find(({ requiredPermission }) => {
    const requiredPermissionsArray = Array.isArray(requiredPermission)
      ? requiredPermission
      : [requiredPermission];

    // NOTED: No required permission at all
    if (requiredPermissionsArray.length === 0) {
      return true;
    }

    return requiredPermissionsArray.some((permission) => permissions.includes(permission));
  });

  if (matchedRoute) {
    const destinationPath =
      'params' in matchedRoute && matchedRoute.params
        ? generatePath(
            matchedRoute.path,
            matchedRoute.params as {
              [key in PathParam<TPath>]: string | null;
            },
          )
        : matchedRoute.path;

    return <Navigate to={destinationPath} replace />;
  }

  return <Navigate to='/access-denied' replace />;
}
